/**
 * useJobTitleMutations Hook
 *
 * 직책(Job Title) 마스터 데이터 CRUD(생성/수정/삭제) Hook입니다.
 *
 * **기능:**
 * - 직책 생성 (createJobTitle)
 * - 직책 수정 (updateJobTitle)
 * - 직책 삭제 (deleteJobTitle) - 소프트 삭제 (status='inactive')
 *
 * **사용 예시:**
 * ```tsx
 * const { createJobTitle, updateJobTitle, deleteJobTitle } = useJobTitleMutations();
 *
 * await createJobTitle({
 *   name: '팀장',
 *   code: 'TEAM_LEADER',
 *   order: 4,
 *   status: 'active',
 * });
 * ```
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthContext } from './useAuthContext';
import {
  CreateJobTitleInput,
  UpdateJobTitleInput,
  mapJobTitleToJobTitleDoc,
} from '../types/jobTitle';
import { invalidateCache, CACHE_KEY } from '../utils/masterDataCache';

/**
 * useJobTitleMutations Hook 반환 타입
 */
export interface UseJobTitleMutationsResult {
  /**
   * 직책 생성
   *
   * @param input 직책 생성 데이터
   * @returns 생성된 직책 ID
   */
  createJobTitle: (input: CreateJobTitleInput) => Promise<string>;

  /**
   * 직책 수정
   *
   * @param jobTitleId 직책 ID
   * @param input 수정할 데이터
   */
  updateJobTitle: (
    jobTitleId: string,
    input: UpdateJobTitleInput
  ) => Promise<void>;

  /**
   * 직책 삭제 (소프트 삭제)
   *
   * @param jobTitleId 직책 ID
   */
  deleteJobTitle: (jobTitleId: string) => Promise<void>;
}

/**
 * 직책 마스터 CRUD Hook
 *
 * @returns UseJobTitleMutationsResult
 */
export function useJobTitleMutations(): UseJobTitleMutationsResult {
  const authContext = useAuthContext();

  /**
   * 직책 생성
   */
  const createJobTitle = async (
    input: CreateJobTitleInput
  ): Promise<string> => {
    if (!db) {
      throw new Error('Firestore가 초기화되지 않았습니다.');
    }

    if (!authContext.userId) {
      throw new Error('로그인이 필요합니다.');
    }

    // 유효성 검사
    if (!input.name || !input.code) {
      throw new Error('직책명과 코드는 필수입니다.');
    }

    try {
      // Firestore 문서 생성
      const docData = {
        name: input.name,
        code: input.code,
        order: input.order,
        status: input.status || 'active',
        isUnique: input.isUnique || false,
        description: input.description || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: authContext.userId,
      };

      const docRef = await addDoc(collection(db, 'jobTitles'), docData);

      // V259: 캐시 무효화
      invalidateCache(CACHE_KEY.JOB_TITLES);

      return docRef.id;
    } catch (err: any) {
      console.error('❌ 직책 생성 실패:', err);
      throw new Error(err.message || '직책 생성에 실패했습니다.');
    }
  };

  /**
   * 직책 수정
   */
  const updateJobTitle = async (
    jobTitleId: string,
    input: UpdateJobTitleInput
  ): Promise<void> => {
    if (!db) {
      throw new Error('Firestore가 초기화되지 않았습니다.');
    }

    if (!authContext.userId) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      const docRef = doc(db, 'jobTitles', jobTitleId);

      // 업데이트할 필드만 포함
      const updateData: any = {
        updatedAt: serverTimestamp(),
        updatedBy: authContext.userId,
      };

      if (input.name !== undefined) updateData.name = input.name;
      if (input.code !== undefined) updateData.code = input.code;
      if (input.order !== undefined) updateData.order = input.order;
      if (input.status !== undefined) updateData.status = input.status;
      if (input.isUnique !== undefined) updateData.isUnique = input.isUnique;
      if (input.description !== undefined)
        updateData.description = input.description;

      await updateDoc(docRef, updateData);

      // V259: 캐시 무효화
      invalidateCache(CACHE_KEY.JOB_TITLES);
    } catch (err: any) {
      console.error('❌ 직책 수정 실패:', err);
      throw new Error(err.message || '직책 수정에 실패했습니다.');
    }
  };

  /**
   * 직책 삭제 (소프트 삭제)
   *
   * **주의:**
   * - 실제 문서를 삭제하지 않고 status='inactive'로 변경
   * - 이미 해당 직책을 가진 직원이 있어도 삭제 가능
   * - 삭제된 직책은 목록에서 표시되지 않지만 데이터는 유지됨
   */
  const deleteJobTitle = async (jobTitleId: string): Promise<void> => {
    if (!db) {
      throw new Error('Firestore가 초기화되지 않았습니다.');
    }

    if (!authContext.userId) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      const docRef = doc(db, 'jobTitles', jobTitleId);

      await updateDoc(docRef, {
        status: 'inactive',
        updatedAt: serverTimestamp(),
        updatedBy: authContext.userId,
      });

      // V259: 캐시 무효화
      invalidateCache(CACHE_KEY.JOB_TITLES);
    } catch (err: any) {
      console.error('❌ 직책 삭제 실패:', err);
      throw new Error(err.message || '직책 삭제에 실패했습니다.');
    }
  };

  return {
    createJobTitle,
    updateJobTitle,
    deleteJobTitle,
  };
}

/**
 * ============================================
 * TODO: 직책 삭제 시 제약 검증 (향후 구현)
 * ============================================
 *
 * **문제점:**
 * - 현재는 직책을 삭제(비활성화)해도 해당 직책을 가진 직원이 있는지 확인하지 않음
 * - 삭제된 직책을 가진 직원의 데이터가 orphan 상태가 될 수 있음
 *
 * **개선 방안:**
 *
 * ```typescript
 * async function deleteJobTitle(jobTitleId: string): Promise<void> {
 *   // 1. 해당 직책을 가진 직원이 있는지 확인
 *   const employeeJobTitles = await getDocs(
 *     query(
 *       collection(db, 'employeeJobTitles'),
 *       where('jobTitleId', '==', jobTitleId),
 *       where('endDate', '==', null)  // 현재 직책만
 *     )
 *   );
 *
 *   if (!employeeJobTitles.empty) {
 *     throw new Error(
 *       `이 직책을 가진 직원이 ${employeeJobTitles.size}명 있습니다. ` +
 *       `직원의 직책을 먼저 변경해주세요.`
 *     );
 *   }
 *
 *   // 2. 직책 삭제 (소프트 삭제)
 *   await updateDoc(doc(db, 'jobTitles', jobTitleId), {
 *     status: 'inactive',
 *     updatedAt: serverTimestamp(),
 *   });
 * }
 * ```
 *
 * **권장:**
 * - 프로덕션 환경에서는 위 검증 로직 구현 권장
 * - 또는 삭제 시 경고 메시지 표시 후 사용자 확인 받기
 */
